/**
 * Componente para exibir grade de jobs/tasks ativos de um projeto específico
 * Dashboard específico para o Gestor
 * 
 * @author Equipe GestaoeCRM
 * @date 03/10/2025
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getActiveOS } from '@/services/osApi';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import { ArrowLeft, Clock, User, Play, Pause, CheckCircle, AlertCircle } from 'lucide-react';

interface ProjectActiveOSGridProps {
  projectId: string;
  projectName: string;
  onBack: () => void;
}

interface ActiveOS {
  id: string;
  orderOfServiceId: string;
  orderOfService: {
    id: string;
    title: string;
    type: string;
    priority: string;
    estimatedHours: number;
    actualHours: number;
    startDate: string;
    demand: {
      id: string;
      title: string;
      project: {
        name: string;
        client: {
          name: string;
        };
      } | null;
    };
  };
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  startTime: string;
  lastHeartbeat: string;
  isActive: boolean;
  currentDuration: number;
  lastHeartbeatMinutes: number;
}

export default function ProjectActiveOSGrid({ projectId, projectName, onBack }: ProjectActiveOSGridProps) {
  const { user } = useAuth();
  const [localActiveOS, setLocalActiveOS] = useState<ActiveOS[]>([]);

  // Buscar jobs/tasks ativos do projeto específico
  const { 
    data: activeOSData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['projectActiveOS', projectId],
    queryFn: () => getActiveOS({ projectId }),
    enabled: !!projectId && user?.role === 'GESTOR',
    refetchInterval: 30000, // Atualizar dados do servidor a cada 30 segundos
  });

  // Atualizar dados locais quando dados do servidor mudarem
  useEffect(() => {
    if (activeOSData?.data) {
      setLocalActiveOS(activeOSData.data);
    }
  }, [activeOSData]);

  // Atualizar duração e heartbeat a cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setLocalActiveOS(prevOS => 
        prevOS.map(os => {
          const now = new Date();
          const startTime = new Date(os.startTime);
          const lastHeartbeat = new Date(os.lastHeartbeat);
          
          return {
            ...os,
            currentDuration: Math.round(((now.getTime() - startTime.getTime()) / (1000 * 60 * 60)) * 100) / 100,
            lastHeartbeatMinutes: Math.round(((now.getTime() - lastHeartbeat.getTime()) / (1000 * 60)) * 100) / 100
          };
        })
      );
    }, 1000); // Atualizar a cada segundo

    return () => clearInterval(interval);
  }, []);

  // Renderizar loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  // Renderizar erro
  if (error) {
    return (
      <div className="space-y-4">
        {/* Header com botão de voltar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Voltar aos Projetos</span>
            </button>
            <div className="h-6 w-px bg-gray-300"></div>
            <h2 className="text-xl font-semibold text-gray-900">
              Jobs/Tasks Ativos - {projectName}
            </h2>
          </div>
        </div>

        {/* Erro */}
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <AlertCircle className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium mb-2">Erro ao carregar jobs/tasks ativos</p>
          <p className="text-sm text-center mb-4">
            Não foi possível carregar os jobs/tasks ativos. Tente novamente.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  const activeOS = localActiveOS;

  return (
    <div className="space-y-6">
      {/* Header com botão de voltar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Voltar aos Projetos</span>
          </button>
          <div className="h-6 w-px bg-gray-300"></div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Jobs/Tasks Ativos - {projectName}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {activeOS.length} ordem{activeOS.length !== 1 ? 'ns' : ''} de serviço ativa{activeOS.length !== 1 ? 's' : ''} no projeto
            </p>
          </div>
        </div>
      </div>

      {/* Grid de Jobs/Tasks Ativos */}
      {activeOS.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <CheckCircle className="h-12 w-12 mb-4" />
          <p className="text-lg font-medium mb-2">Nenhum job/task ativo</p>
          <p className="text-sm text-center">
            Não há jobs/tasks ativos neste projeto no momento.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Header da Grade */}
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-3">OS / Funcionário</div>
              <div className="col-span-2">Tipo / Demanda</div>
              <div className="col-span-2">Cliente</div>
              <div className="col-span-2">Status / Duração</div>
              <div className="col-span-2">Horas</div>
              <div className="col-span-1">Heartbeat</div>
            </div>
          </div>

          {/* Conteúdo da Grade */}
          <div className="bg-white divide-y divide-gray-200">
            {activeOS.map((os) => (
              <div key={os.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="grid grid-cols-12 gap-4 items-center">
                  {/* OS / Funcionário */}
                  <div className="col-span-3">
                    <div className="flex items-center space-x-1">
                      <Play className="h-4 w-4 text-green-600" />
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {os.orderOfService.title}
                        </h4>
                        <div className="flex items-center space-x-1 mt-1">
                          <User className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-600">
                            {os.user.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tipo / Demanda */}
                  <div className="col-span-2">
                    <div className="text-sm text-gray-900">
                      {os.orderOfService.type}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {os.orderOfService.demand.title}
                    </div>
                  </div>

                  {/* Cliente */}
                  <div className="col-span-2">
                    <div className="text-sm text-gray-900 truncate">
                      {os.orderOfService.demand.project?.client.name || '-'}
                    </div>
                  </div>

                  {/* Status / Duração */}
                  <div className="col-span-2">
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Ativa
                      </span>
                      <div className="text-sm font-mono text-green-700">
                        {Math.floor(os.currentDuration * 60)}m
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(os.startTime).toLocaleDateString('pt-BR')}, {new Date(os.startTime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>

                  {/* Horas */}
                  <div className="col-span-2">
                    <div className="text-sm text-gray-900">
                      {os.orderOfService.actualHours.toFixed(2)}h
                    </div>
                    <div className="text-xs text-gray-500">
                      / {os.orderOfService.estimatedHours}h
                    </div>
                  </div>

                  {/* Heartbeat */}
                  <div className="col-span-1">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs text-gray-500">
                        {os.lastHeartbeatMinutes.toFixed(1)}min
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
